import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { useNavigate, useParams,useLocation} from "react-router-dom";
import { DateTime } from "luxon";

import { useState } from "react";
import type { IResponse } from "../types/response.types";

// Utility to merge class names
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Utility to format bytes
export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {},
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(decimals)} ${
    sizeType === "accurate" ? accurateSizes[i] ?? "Bytest" : sizes[i] ?? "Bytes"
  }`;
}

// Helper function to get ordinal suffix
const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return "th"; // Handle 11th, 12th, 13th
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const formatDate = (date: string) => {
  if (!date) return "";

  // Convert date to IST
  const dateTime = DateTime.fromISO(date).setZone("Asia/Kolkata");

  // Format the date with ordinal suffix
  const day = dateTime.toFormat("d");
  const suffix = getOrdinalSuffix(Number.parseInt(day));
  const formattedDay = `${day}${suffix}`;

  const month = dateTime.toFormat("MMM");
  const year = dateTime.toFormat("yyyy");

  return `${formattedDay} ${month} ${year}`;
};

// Debounce utility function
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

// Dynamic navigation with a delay and open state
export function useHandleNavigate(navigatePath: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId, postId, favoriteId, brandId, sampleId, collabId, applicationId, proposalId, proposalSocialId, vendorId, tab } = useParams();
  const [open, setOpen] = useState(true);

  // Replace dynamic params in navigatePath
  const resolvedNavigatePath = navigatePath
    .replace(":campaignId", campaignId ?? "")
    .replace(":postId", postId ?? "")
    .replace(":favoriteId", favoriteId ?? "")
    .replace(":brandId", brandId ?? "")
    .replace(":sampleId", sampleId ?? "")
    .replace(":collabId", collabId ?? "")
    .replace(":applicationId", applicationId ?? "")
    .replace(":proposalId", proposalId ?? "")
    .replace(":proposalSocialId", proposalSocialId ?? "")
    .replace(":vendorId", vendorId ?? "")
    .replace(":tab", tab ?? "");

  // Function to handle navigation with a 200ms delay
  function handleNavigate() {
    setOpen(false);
    setTimeout(() => {
      navigate(`${resolvedNavigatePath}${location.search}`);
    }, 200);
  }

  return { open, setOpen, handleNavigate };
}

export function handleHttpError<T>(
  response: IResponse<T>,
  toast: (options: {
    content: string;
    toastType: "success" | "error" | undefined;
  }) => void,
) {
  if (response.ignoreError) {
    return;
  }
  if (!response.success) {
    return toast({
      content: response.error.message,
      toastType: "error",
    });
  }
}

// Function to get color for each bar
export const getBarColor = (index: number) => {
  const colors = ["#2A65DF", "#96ff43", "#FF404E", "#FFF136"];
  return colors[index % colors.length];
};

// Utility function using Luxon to calculate startDate and endDate
export function getDateRange(range: string) {
  const endDate = DateTime.now().toISO();
  // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
  let startDate: string;

  switch (range) {
    case "Last 7 days":
      startDate = DateTime.now().minus({ days: 7 }).toISO();
      break;
    case "Last 30 days":
      startDate = DateTime.now().minus({ days: 30 }).toISO();
      break;
    case "Last 90 days":
      startDate = DateTime.now().minus({ days: 90 }).toISO();
      break;
    case "Last 1 year":
      startDate = DateTime.now().minus({ years: 1 }).toISO();
      break;
    case "This month":
      startDate = DateTime.now().startOf("month").toISO();
      break;
    case "All time":
      startDate = DateTime.fromISO("2023-11-06T13:07:04.682Z").toISO() || "";
      break;
    default:
      startDate = DateTime.now().minus({ days: 7 }).toISO();
  }

  return { startDate, endDate };
}

// Function to recursively find the error message
export const findErrorMessage = (error: any): string | null => {
  if (typeof error === "object" && error !== null) {
    if ("message" in error) {
      return error.message;
    }
    // Recursively check all nested keys
    for (const key in error) {
      const nestedMessage = findErrorMessage(error[key]);
      if (nestedMessage) return nestedMessage;
    }
  }
  return null;
};

export function deepEqualObjects(
  obj1: Record<string, any>,
  obj2: Record<string, any>,
): boolean {
  if (obj1 === obj2) return true; // Reference equality
  if (
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 === null ||
    obj2 === null
  )
    return false; // Ensure both are objects

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false; // Different number of keys

  for (const key of keys1) {
    if (!keys2.includes(key)) return false; // Key mismatch
    if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
      if (!deepEqualArrays(obj1[key], obj2[key])) return false;
    } else if (
      obj1[key] !== null &&
      obj2[key] !== null &&
      typeof obj1[key] === "object" &&
      typeof obj2[key] === "object"
    ) {
      if (!deepEqualObjects(obj1[key], obj2[key])) return false;
    } else {
      if (obj1[key] !== obj2[key]) return false; // Primitive mismatch
    }
  }

  return true;
}

export function deepEqualArrays(arr1: any[], arr2: any[]): boolean {
  if (arr1 === arr2) return true; // Reference equality
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false; // Both must be arrays
  if (arr1.length !== arr2.length) return false; // Different lengths

  // Compare each element
  for (let i = 0; i < arr1.length; i++) {
    const elem1 = arr1[i];
    const elem2 = arr2[i];

    // If elements are arrays or objects, recursively compare them
    if (Array.isArray(elem1) && Array.isArray(elem2)) {
      if (!deepEqualArrays(elem1, elem2)) return false;
    } else if (
      elem1 !== null &&
      elem2 !== null &&
      typeof elem1 === "object" &&
      typeof elem2 === "object"
    ) {
      if (!deepEqualObjects(elem1, elem2)) return false;
    } else {
      // Primitive value comparison
      if (elem1 !== elem2) return false;
    }
  }

  return true;
}

export function autoReviewStatus(status: string) {
  if (status === "pass") return "No action taken";
  if (status === "approved") return "Post auto approved";
  if (status === "rejected") return "Post auto rejected";
  if (status === "pending") return "Post is being processed";
  if (status === "error") return "Error";
  return "Unknown status";
}

export const getCurrentMonth = () => {
  const months = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ];
  return months[new Date().getMonth()];
};

/**
 * Converts empty, null, or 0 values to undefined
 */
export function sanitizeNumberField(
  value: number | string | null | undefined,
): number | undefined {
  if (value === null || value === undefined || value === "" || value === 0) {
    return undefined;
  }

  const num = typeof value === "string" ? Number.parseInt(value, 10) : value;
  return Number.isNaN(num) || num === 0 ? undefined : num;
}

let currentlyPlayingVideo: HTMLVideoElement | null = null;

/**
 * Ensures only one <video> element plays at a time across the app.
 * Call from a video's onPlay handler: onPlay={(e) => pauseOtherVideos(e.currentTarget)}
 */
export function pauseOtherVideos(video: HTMLVideoElement): void {
  if (currentlyPlayingVideo && currentlyPlayingVideo !== video) {
    currentlyPlayingVideo.pause();
  }
  currentlyPlayingVideo = video;
}

/**
 * Pauses whatever video is currently playing, if any.
 * Call this when a UI surface opens that should not overlap with playback
 * elsewhere (e.g. opening the post detail slider).
 */
export function pauseCurrentVideo(): void {
  if (currentlyPlayingVideo) {
    currentlyPlayingVideo.pause();
    currentlyPlayingVideo = null;
  }
}
